import React from 'react';
import PropTypes from 'prop-types';
import Waypoint from 'react-waypoint';
import Raven from 'raven-js';
import { Route, withRouter } from 'react-router-dom';
import Alert from 'react-s-alert';
import debounce from 'lodash/debounce';
import { generatePath } from 'react-router';

import Withdraws from 'api/resources/withdraws';
import getErrors, { processErrorByAlert } from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';
import fullname from 'utils/fullname';

import UserTransactionsPage from 'pages/UserTransactionsPage';

import UserRow from 'admin-moderator/containers/UserRow';

import Loader from 'components/Loader';
import Checkbox from 'components/Checkbox';
import SearchForm from 'components/SearchForm';
import FiltersDropdown from 'components/FiltersDropdown';

import ErrorText from 'styled/Page/ErrorText';
import CenteredContainer from 'styled/Page/CenteredContainer';
import SimpleList from 'styled/SimpleList';
import PrimaryButton from 'styled/Buttons/PrimaryButton';
import SecondaryButton from 'styled/Buttons/SecondaryButton';
import FilterWrapper from 'styled/Page/FilterWrapper';

import CheckAllBlock from './CheckAllBlock';
import RightColumnStyled from './RightColumnStyled';

const PAGE_SIZE = 20;

const FILTERS = [
  {
    label: 'Необработанные',
    value: 'created',
  }, {
    label: 'Одобренные',
    value: 'processing,finished',
  }, {
    label: 'Отклоненные',
    value: 'rejected',
  }, {
    label: 'С ошибкой',
    value: 'failed,failed_by_notification',
  },
];

export class WithdrawalRequests extends React.PureComponent {
  state = {
    loading: false,
    errors: [],
    hasMore: false,
    withdraws: [],
    status: FILTERS.find(item => item.value === this.props.match.params.status),
    searchQuery: '',
    checkedWithdraws: [],
    updateLoading: false,
  }

  loadWithdrawsWithDebounce = debounce(() => {
    this.reloadWithdraws();
  }, 500);

  componentDidMount() {
    // если пришли по прямой ссылке, то не открываем первый запрос при загрузке списка
    this.loadWithdraws({ shouldOpenFirstWithdraw: this.props.history.location.pathname.indexOf('/user/') === -1 });
  }

  componentDidUpdate(prevProps) {
    if (this.props.match.params.status !== prevProps.match.params.status) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        status: FILTERS.find(item => item.value === this.props.match.params.status),
      });
      this.reloadWithdraws();
    }
  }

  get cursor() {
    if (this.state.withdraws.length === 0) return undefined;
    return this.state.withdraws[this.state.withdraws.length - 1].id;
  }

  redirectToGeneratePath = (params) => {
    this.props.history.push(
      generatePath(
        '/biller/:department/status/:status/:requestId/user/:userId',
        {
          ...this.props.match.params,
          ...params,
        }
      )
    );
  }

  handleFilterChange = (status) => {
    if (this.state.status === status) return;
    this.props.history.push(`/biller/requests/status/${status.value}`);
  }

  handleSearchChange = ({ target }) => {
    this.setState({
      searchQuery: target.value,
    }, this.loadWithdrawsWithDebounce);
  }

  handleSearchClose = () => {
    this.setState({
      searchQuery: undefined,
      withdraws: [],
      loading: true,
      hasMore: false,
    }, () => this.loadWithdraws({ shouldOpenFirstWithdraw: true }));
  }

  handleSearchSubmit = (e) => {
    e.preventDefault();

    if (this.state.loading) return;

    this.reloadWithdraws();
  }

  handleCheckAllChange = ({ target }) => {
    this.setState(prevState => ({
      checkedWithdraws: target.checked
        ? prevState.withdraws.map(withdraw => withdraw.id)
        : [],
    }));
  }

  handleCheckChange = ({ target }) => {
    this.setState(prevState => ({
      checkedWithdraws: target.checked
        ? prevState.checkedWithdraws.concat(target.id)
        : prevState.checkedWithdraws.filter(item => item !== target.id),
    }));
  }

  handleStatusUpdate = (withdraw) => {
    let withdrawPosition = this.state.withdraws.findIndex(item => item.id === withdraw.id);
    if (this.state.withdraws.length === withdrawPosition + 1) {
      withdrawPosition -= 1;
    }

    this.setState(prevState => ({
      withdraws: prevState.withdraws.filter(item => item.id !== withdraw.id),
    }), () => this.showNextRequestInfo(withdrawPosition));
  }

  showNextRequestInfo = (withdrawPosition) => {
    if (!this.state.withdraws[withdrawPosition]
      || !this.state.withdraws[withdrawPosition].user
      || !this.state.withdraws[withdrawPosition].user.id
    ) {
      this.props.history.push(`/biller/requests/status/${this.props.match.params.status}`);
      return;
    }

    this.redirectToGeneratePath({
      requestId: this.state.withdraws[withdrawPosition].id,
      userId: this.state.withdraws[withdrawPosition].user.id,
    });
  }

  reloadWithdraws = () => {
    this.setState({
      withdraws: [],
      checkedWithdraws: [],
      hasMore: false,
    }, () => this.loadWithdraws({ shouldOpenFirstWithdraw: true }));
  }

  loadWithdraws = async ({ shouldOpenFirstWithdraw } = {}) => {
    this.setState({ loading: true, errors: [] });

    try {
      const response = await Withdraws.getAll({
        page: {
          size: PAGE_SIZE,
          cursor: this.cursor,
        },
        filter: {
          status: this.props.match.params.status,
          q: this.state.searchQuery,
        },
        include: 'user,user.avatar,user.oauth_providers',
        fields: {
          user: 'first_name,last_name,avatar,oauth_providers',
        },
      });

      const withdraws = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      if (shouldOpenFirstWithdraw) {
        if (withdraws.length > 0 && withdraws[0].user && withdraws[0].user.id) {
          this.redirectToGeneratePath({
            requestId: withdraws[0].id,
            userId: withdraws[0].user.id,
          });
        } else {
          this.props.history.push(
            generatePath(
              '/biller/:department/status/:status',
              { ...this.props.match.params }
            )
          );
        }
      }

      this.setState(prevState => ({
        withdraws: withdraws.reduce((acc, currentValue) => {
          if (acc.find(item => item.id === currentValue.id)) {
            return acc;
          }

          return acc.concat(currentValue);
        }, prevState.withdraws),
        loading: false,
        hasMore: withdraws.length === PAGE_SIZE,
      }));
    } catch (err) {
      this.setState({ errors: await getErrors(err), loading: false });

      Raven.captureException(err, { extra: this.props });
    }
  }

  handleProcessingClick = () => {
    this.massUpdate('processing');
  }

  handleRejectClick = () => {
    this.massUpdate('rejected');
  }

  massUpdate = async (status) => {
    if (this.state.checkedWithdraws.length === 0) {
      Alert.warning('Не выбран ни один запрос');
      return;
    }

    this.setState({ updateLoading: true });

    try {
      await Withdraws.massUpdate({
        body: {
          data: {
            type: 'massive_withdraw',
            attributes: {
              status,
              ids: this.state.checkedWithdraws,
            },
          },
        },
      });

      Alert.success(`Запросы на вывод средств ${status === 'rejected' ? 'отклонены' : 'одобрены'}`);
      this.setState({ updateLoading: false, checkedWithdraws: [] });
      this.reloadWithdraws();
    } catch (err) {
      processErrorByAlert(err);
      this.setState({ updateLoading: false });

      Raven.captureException(err, { extra: this.props });
    }
  }

  render() {
    return (
      <div style={{ display: 'flex', paddingTop: '16px' }}>
        <div style={{ width: '100%' }}>
          <FilterWrapper style={{ top: '50px', margin: '0 0 24px 0', paddingTop: 0 }}>
            <FiltersDropdown
              filters={FILTERS}
              onFilterChange={this.handleFilterChange}
              style={{ width: '170px', marginRight: 'auto' }}
              activeFilter={this.state.status}
            />

            <SearchForm
              onChange={this.handleSearchChange}
              onSubmit={this.handleSearchSubmit}
              onClose={this.handleSearchClose}
              value={this.state.searchQuery}
            />
          </FilterWrapper>

          {this.state.withdraws.length !== 0 && this.state.status.value === 'created' && (
            <React.Fragment>
              <CheckAllBlock>
                <Checkbox
                  id="isCheckedAll"
                  checked={this.state.checkedWithdraws.length === this.state.withdraws.length}
                  onChange={this.handleCheckAllChange}
                  toggle
                  disabled={this.state.loading || this.state.updateLoading}
                >
                  Отметить всех
                </Checkbox>

                <PrimaryButton
                  type="button"
                  style={{ marginRight: '8px', marginLeft: 'auto' }}
                  onClick={this.handleProcessingClick}
                  disabled={this.state.loading || this.state.updateLoading}
                >
                  оплатить
                </PrimaryButton>

                <SecondaryButton
                  type="button"
                  onClick={this.handleRejectClick}
                  disabled={this.state.loading || this.state.updateLoading}
                >
                  отклонить
                </SecondaryButton>
              </CheckAllBlock>
            </React.Fragment>
          )}

          <SimpleList>
            {this.state.withdraws.map(withdraw => (
              <UserRow
                key={withdraw.id}
                id={withdraw.id}
                name={fullname(withdraw.user)}
                isChecked={this.state.checkedWithdraws.includes(withdraw.id)}
                amount={withdraw.amount}
                method={withdraw.method}
                methodInfo={withdraw.methodInfo}
                avatar={withdraw.user && withdraw.user.avatar && withdraw.user.avatar.url}
                socialLinks={withdraw.user && withdraw.user.oauthProviders}
                onCheckboxChange={this.handleCheckChange}
                onStatusUpdate={this.handleStatusUpdate}
                disabled={this.state.loading || this.state.updateLoading}
                currency={withdraw.currency}
                userId={withdraw.user && withdraw.user.id}
              />
            ))}

            {!this.state.loading && !this.state.errors.length && this.state.hasMore && (
              <Waypoint onEnter={this.loadWithdraws} />
            )}
          </SimpleList>

          {this.state.withdraws.length === 0
            && !this.state.loading
            && this.state.errors.length === 0 && (
            <CenteredContainer>
              <ErrorText>Нет запросов на вывод средств</ErrorText>
            </CenteredContainer>
          )}

          {this.state.loading && (<CenteredContainer><Loader /></CenteredContainer>)}

          {!this.state.loading && this.state.errors.length > 0 && (
            <CenteredContainer>
              {this.state.errors.map(error => (
                <ErrorText role="alert" key={error}>{error}</ErrorText>
              ))}
            </CenteredContainer>
          )}
        </div>

        <RightColumnStyled>
          <Route
            path="/biller/:department(requests)/status/:status(created|processing,finished|rejected|failed,failed_by_notification)/:requestId/user/:userId"
            render={() => (
              <UserTransactionsPage
                shouldTransactionsUpdate={this.props.shouldTransactionsUpdate}
                onTransactionsUpdate={this.props.onTransactionsUpdate}
              />
            )}
            exact
          />
        </RightColumnStyled>
      </div>
    );
  }
}

WithdrawalRequests.propTypes = {
  shouldTransactionsUpdate: PropTypes.bool,
  onTransactionsUpdate: PropTypes.func,
  history: PropTypes.shape({
    location: PropTypes.shape({ pathname: PropTypes.string }),
    push: PropTypes.func,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      status: PropTypes.oneOf(['created', 'processing,finished', 'rejected', 'failed,failed_by_notification']),
    }),
  }).isRequired,
};

WithdrawalRequests.defaultProps = {
  shouldTransactionsUpdate: false,
  onTransactionsUpdate: () => {},
  history: { location: { pathname: '', push: () => {} } },
};

export default withRouter(WithdrawalRequests);
